import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { Plus, Trash2, RotateCcw, Save, Loader2, GripVertical } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const MAX_DIMENSIONS = 5;

// The built-in rubric the model uses today. Editing here lets an org tailor it to their own standards.
export const DEFAULT_RUBRIC = [
  {
    key: 'clarity',
    label: 'Clarity',
    description: 'Did the tutor explain things in a way the student could follow?',
    levels: {
      strong: "Explanations were clear, well-structured, appropriate to student's level. Student demonstrated understanding.",
      adequate: 'Explanations were understandable. Some minor confusion but generally effective.',
      needs_development: 'Student was frequently confused. Explanations were unclear, too fast, or poorly structured.',
    },
  },
  {
    key: 'engagement',
    label: 'Engagement',
    description: 'Was the student actively participating or passive/silent?',
    levels: {
      strong: 'Student actively participated — asking questions, attempting problems, volunteering thoughts.',
      adequate: 'Student responded when prompted. Followed along. Not highly active but not disengaged.',
      needs_development: 'Student was passive, silent, or disengaged. Tutor did most of the talking with minimal interaction.',
    },
  },
  {
    key: 'adaptability',
    label: 'Adaptability',
    description: 'Did the tutor adjust their approach when the student struggled?',
    levels: {
      strong: "When student struggled, tutor tried different explanations, examples, or approaches. Responsive to student's needs.",
      adequate: 'Some adjustment when needed. Generally responsive.',
      needs_development: "Tutor repeated same explanation when student didn't understand. Inflexible approach.",
    },
  },
  {
    key: 'encouragement',
    label: 'Encouragement',
    description: 'Did the tutor build confidence or create anxiety?',
    levels: {
      strong: "Warm, encouraging tone. Praised effort and progress. Built student's confidence.",
      adequate: 'Professional and supportive. No discouragement.',
      needs_development: 'Cold, critical, or impatient tone. Student may have felt discouraged or anxious.',
    },
  },
  {
    key: 'structure',
    label: 'Structure',
    description: 'Was there a sense of progression, or aimless wandering?',
    levels: {
      strong: 'Clear lesson flow — introduction, main content, recap/next steps. Well-paced.',
      adequate: 'Reasonable structure. Content made sense.',
      needs_development: 'Disorganised. Jumped between topics. No clear progression or conclusion.',
    },
  },
];

const RATING_LEVELS = [
  { key: 'strong', label: 'Strong', tone: 'emerald' },
  { key: 'adequate', label: 'Adequate', tone: 'amber' },
  { key: 'needs_development', label: 'Needs development', tone: 'rose' },
];

const slugify = (s) =>
  (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'dimension';

const blankDimension = (i) => ({
  key: `dimension_${i}`,
  label: '',
  description: '',
  levels: { strong: '', adequate: '', needs_development: '' },
});

const QualityRubric = ({ embedded = false } = {}) => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [dims, setDims] = useState(DEFAULT_RUBRIC);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    (async () => {
      const { data } = await supabase
        .from('organizations')
        .select('quality_rubric')
        .eq('id', organizationId)
        .single();
      if (Array.isArray(data?.quality_rubric) && data.quality_rubric.length) {
        setDims(data.quality_rubric);
        setIsCustom(true);
      } else {
        setDims(DEFAULT_RUBRIC);
        setIsCustom(false);
      }
      setLoading(false);
    })();
  }, [organizationId]);

  const update = (next) => {
    setDims(next);
    setDirty(true);
  };

  const setLabel = (i, value) =>
    update(dims.map((d, idx) => (idx === i ? { ...d, label: value } : d)));
  const setDescription = (i, value) =>
    update(dims.map((d, idx) => (idx === i ? { ...d, description: value } : d)));
  const setLevel = (i, levelKey, value) =>
    update(dims.map((d, idx) => (idx === i ? { ...d, levels: { ...d.levels, [levelKey]: value } } : d)));

  const addDimension = () => {
    if (dims.length >= MAX_DIMENSIONS) return;
    update([...dims, blankDimension(dims.length + 1)]);
  };
  const removeDimension = (i) => update(dims.filter((_, idx) => idx !== i));

  const resetDefault = () => {
    if (!window.confirm('Reset the rubric to the built-in default? Your custom criteria will be replaced.')) return;
    update(JSON.parse(JSON.stringify(DEFAULT_RUBRIC)));
  };

  const save = async () => {
    const cleaned = dims
      .filter((d) => d.label.trim())
      .map((d) => ({ ...d, key: d.key && d.key.startsWith('dimension_') ? slugify(d.label) : d.key || slugify(d.label) }));
    if (!cleaned.length) {
      toast.error('Add at least one dimension with a name.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ quality_rubric: cleaned })
        .eq('id', organizationId);
      if (error) throw error;
      setDims(cleaned);
      setIsCustom(true);
      setDirty(false);
      toast.success('Quality rubric saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save rubric');
    }
    setSaving(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full text-sm rounded-md border px-2.5 py-1.5 outline-none transition-colors ${
    isDark
      ? 'bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#3f3f46]'
      : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder:text-[#aaa] focus:border-[#ccc]'
  }`;
  const toneClass = (tone) =>
    tone === 'emerald'
      ? isDark ? 'text-emerald-400' : 'text-emerald-600'
      : tone === 'amber'
      ? isDark ? 'text-amber-400' : 'text-amber-600'
      : isDark ? 'text-rose-400' : 'text-rose-500';

  if (loading) {
    return <div className={`${cardClass} h-48 animate-pulse`} />;
  }

  return (
    <div className="space-y-5">
      {/* Intro */}
      <div className={`${cardClass} p-5`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              Quality rubric
            </h3>
            <p className={`text-sm mt-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              Define the criteria TutorTracer assesses every lesson against. Each lesson is rated{' '}
              <span className={toneClass('emerald')}>Strong</span> /{' '}
              <span className={toneClass('amber')}>Adequate</span> /{' '}
              <span className={toneClass('rose')}>Needs development</span> on each dimension, with supporting
              evidence from the transcript. Set both the dimension and what each rating means for your standards.
            </p>
          </div>
          <span
            className={`shrink-0 text-xs px-2 py-1 rounded-full ${
              isCustom
                ? isDark ? 'bg-[#7662ac]/20 text-[#b9a8e0]' : 'bg-[#586f8d]/15 text-[#586f8d]'
                : isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#f0f0f0] text-[#666]'
            }`}
          >
            {isCustom ? 'Custom rubric' : 'Default rubric'}
          </span>
        </div>
      </div>

      {/* Table header (md+) */}
      <div className={`hidden md:grid grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3 px-1 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
        <div>Dimension</div>
        {RATING_LEVELS.map((l) => (
          <div key={l.key} className={toneClass(l.tone)}>{l.label}</div>
        ))}
        <div />
      </div>

      {/* Rows */}
      <div className="space-y-3">
        {dims.map((d, i) => (
          <div key={i} className={`${cardClass} p-4`}>
            <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3 items-start">
              {/* Dimension name + description */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <GripVertical className={`h-4 w-4 shrink-0 ${isDark ? 'text-[#3f3f46]' : 'text-[#ccc]'}`} />
                  <input
                    value={d.label}
                    onChange={(e) => setLabel(i, e.target.value)}
                    placeholder="Dimension name"
                    className={`${inputClass} font-medium`}
                  />
                </div>
                <Textarea
                  value={d.description}
                  onChange={(e) => setDescription(i, e.target.value)}
                  placeholder="What this dimension measures (a short question)"
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Rating-level definitions */}
              {RATING_LEVELS.map((l) => (
                <div key={l.key} className="space-y-1">
                  <div className={`md:hidden text-xs font-medium ${toneClass(l.tone)}`}>{l.label}</div>
                  <Textarea
                    value={d.levels?.[l.key] || ''}
                    onChange={(e) => setLevel(i, l.key, e.target.value)}
                    placeholder={`What "${l.label}" looks like`}
                    rows={4}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              ))}

              {/* Remove */}
              <button
                onClick={() => removeDimension(i)}
                title="Remove dimension"
                className={`p-2 rounded-md transition-colors ${isDark ? 'text-[#52525b] hover:text-rose-400 hover:bg-[#27272a]' : 'text-[#999] hover:text-rose-500 hover:bg-[#f0f0f0]'}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={addDimension}
            disabled={dims.length >= MAX_DIMENSIONS}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:bg-[#f9f9f9]'}`}
          >
            <Plus className="h-4 w-4" /> Add dimension
          </button>
          <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            {dims.length}/{MAX_DIMENSIONS} dimensions
          </span>
          <button
            onClick={resetDefault}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-md transition-colors ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'text-[#666] hover:text-[#1a1a1a] hover:bg-[#f0f0f0]'}`}
          >
            <RotateCcw className="h-3 w-3" /> Reset to default
          </button>
        </div>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? 'bg-white text-[#1a1a1a] hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save rubric
        </button>
      </div>
    </div>
  );
};

export default QualityRubric;
